import React, { useCallback, useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Loader2, MessageSquare, Inbox, CheckCircle2, Sparkles, Send } from "lucide-react";

const FILTERS = ["all", "unread", "campaign", "organic"];

export default function RepliesInbox({ onOpenContact }) {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("all");
  const [sendingId, setSendingId] = useState(null);
  const [editing, setEditing] = useState({}); // id -> current text

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/replies?filter=${filter}`);
      setRows(r.data?.replies || []);
    } catch (e) {
      setRows([]);
    }
  }, [filter]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const markRead = async (id) => {
    try {
      await api.post(`/replies/${id}/mark-read`);
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, read: true } : r)));
    } catch (e) { /* silent */ }
  };

  const regenerateDraft = async (row) => {
    try {
      const r = await api.post("/replies/draft", { ghl_contact_id: row.ghl_contact_id, inbound_message_id: row.id });
      const draft = r.data?.draft || "";
      setEditing((prev) => ({ ...prev, [row.id]: draft }));
      setRows((prev) => prev.map((x) => (x.id === row.id ? { ...x, draft_reply: draft } : x)));
      toast.success("Draft refreshed.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Draft failed.");
    }
  };

  const sendReply = async (row) => {
    const text = (editing[row.id] ?? row.draft_reply ?? "").trim();
    if (!text) {
      toast.error("Type a reply first.");
      return;
    }
    setSendingId(row.id);
    try {
      await api.post(`/contacts/${row.ghl_contact_id}/send-message`, { message: text, channel: "WhatsApp" });
      await markRead(row.id);
      toast.success("Reply sent — logged in the timeline.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed.");
    } finally {
      setSendingId(null);
    }
  };

  return (
    <div className="rounded-2xl border border-zinc-900 bg-zinc-950/60 p-6" data-testid="replies-inbox">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-emerald-400">// Two-Way Loop</div>
          <h2 className="font-display font-black text-2xl text-white tracking-tight mt-1 inline-flex items-center gap-2">
            <Inbox className="w-6 h-6 text-emerald-300" />
            Replies
          </h2>
          <p className="text-xs text-zinc-500 mt-1">Inbound WhatsApp messages with AI-drafted responses ready to send.</p>
        </div>
        <div className="flex items-center gap-1">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`replies-filter-${f}`}
              className={`text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full border transition-colors ${
                filter === f
                  ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                  : "border-zinc-800 text-zinc-500 hover:text-zinc-300"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>
      {rows === null ? (
        <div className="text-sm text-zinc-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-zinc-500 py-8 text-center border border-dashed border-zinc-800 rounded-lg">
          No replies yet. Inbound WhatsApp messages will appear here.
        </div>
      ) : (
        <div className="space-y-3" data-testid="replies-list">
          {rows.map((r) => {
            const current = editing[r.id] ?? r.draft_reply ?? "";
            return (
              <div
                key={r.id}
                data-testid={`reply-${r.id}`}
                className={`rounded-lg border p-4 ${r.read ? "border-zinc-900 bg-zinc-900/20" : "border-emerald-500/30 bg-emerald-500/5"}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <MessageSquare className="w-4 h-4 text-emerald-300" />
                  <span className="text-sm text-white font-medium">{r.contact_name || r.phone}</span>
                  {r.batch_linked && (
                    <span className="text-[10px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded border border-amber-500/30 text-amber-300">campaign reply</span>
                  )}
                  {!r.read && (
                    <span className="text-[10px] uppercase tracking-[0.18em] text-emerald-300 ml-auto">unread</span>
                  )}
                  <span className="text-[10px] text-zinc-500 ml-auto">{new Date(r.received_at).toLocaleString()}</span>
                </div>
                <div className="text-sm text-zinc-300 my-2 whitespace-pre-wrap">{r.text}</div>
                <div className="mt-3 pt-3 border-t border-zinc-900">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 inline-flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> Suggested reply
                    </span>
                    <button
                      onClick={() => regenerateDraft(r)}
                      data-testid={`reply-regenerate-${r.id}`}
                      className="text-[10px] uppercase tracking-[0.18em] text-emerald-400 hover:text-emerald-200"
                    >Regenerate</button>
                  </div>
                  <textarea
                    data-testid={`reply-textarea-${r.id}`}
                    value={current}
                    onChange={(e) => setEditing((p) => ({ ...p, [r.id]: e.target.value }))}
                    rows={3}
                    placeholder="Draft is being prepared…"
                    className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm text-white"
                  />
                  <div className="flex justify-between mt-2">
                    <button
                      onClick={() => (onOpenContact ? onOpenContact(r.ghl_contact_id) : null)}
                      className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 hover:text-zinc-300"
                      data-testid={`reply-open-${r.id}`}
                    >Open timeline →</button>
                    <div className="flex items-center gap-2">
                      {!r.read && (
                        <button
                          onClick={() => markRead(r.id)}
                          data-testid={`reply-mark-read-${r.id}`}
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-zinc-400 border border-zinc-800 rounded px-2 py-1"
                        ><CheckCircle2 className="w-3 h-3" /> Mark read</button>
                      )}
                      <button
                        onClick={() => sendReply(r)}
                        disabled={sendingId === r.id}
                        data-testid={`reply-send-${r.id}`}
                        className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 disabled:opacity-40"
                      >
                        {sendingId === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                        Send reply
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
